import { API_URL } from '../config/config';
import { getRequest, postRequest } from '../utils/axiosUtils';

/**
 * AI Shortlist (profile curation) data.
 *
 * GET /admin/curation/shortlist — stats + the three buckets, resolved
 * server-side (admin correction outranks the AI verdict):
 *   approved = elite shortlist (premium photo + affluence signal)
 *   maybe    = approved by photo but no elite signal
 *   rejected = photo reject (selfie / dark / AI-generated / low-effort)
 * `q` and `sort` are applied to the WHOLE judged population before the
 * per-bucket cap, so a bucket bigger than `limit` still searches and sorts in
 * full. `counts` then describe the matches and `totals` the population they
 * were drawn from.
 *
 * POST /admin/curation/bucket/:reviewId { bucket, note } — records an admin
 * correction and applies the search policy for that member immediately.
 * The note is optional and explains WHY the AI was overruled; it is kept with
 * the correction history so the rubric can later be sharpened from real
 * disagreements rather than from memory.
 */
export const getShortlist = async (limit = 50, { location, joined, from, to, q, sort } = {}) => {
  const qs = [`limit=${encodeURIComponent(limit)}`];
  if (location && location !== 'all') qs.push(`location=${encodeURIComponent(location)}`);
  if (joined && joined !== 'all') qs.push(`joined=${encodeURIComponent(joined)}`);
  if (joined === 'custom') {
    if (from) qs.push(`from=${encodeURIComponent(from)}`);
    if (to) qs.push(`to=${encodeURIComponent(to)}`);
  }
  if (q) qs.push(`q=${encodeURIComponent(q)}`);
  if (sort) qs.push(`sort=${encodeURIComponent(sort)}`);
  const res = await getRequest({
    url: `${API_URL}/admin/curation/shortlist?${qs.join('&')}`,
    isAbort: false,
  });
  if (!res) return null;
  if (res.status >= 200 && res.status < 300 && res.data) return res.data.data;
  throw new Error(`Unexpected response code: ${res.status}`);
};

// Bulk-applies the search policy (approved in search; maybe/rejected hidden).
// Returns a summary { batchId, hidden, unhidden, skipped..., errors }.
export const applyShortlist = async () => {
  const res = await postRequest({
    url: `${API_URL}/admin/curation/apply`,
    data: {},
  });
  if (!res) return null;
  if (res.status >= 200 && res.status < 300 && res.data) return res.data.data;
  throw new Error(`Unexpected response code: ${res.status}`);
};

export const setBucket = async (reviewId, bucket, note = '') => {
  const res = await postRequest({
    url: `${API_URL}/admin/curation/bucket/${reviewId}`,
    data: { bucket, note },
  });
  if (!res) return null;
  if (res.status >= 200 && res.status < 300 && res.data) return res.data.data;
  throw new Error(`Unexpected response code: ${res.status}`);
};
